import { Check, ChevronRight, OctagonAlert, OctagonPause, Repeat } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useRoutines } from '../../../lib/routines';
import { routineStatus } from '../../../lib/routine-status';
import { useNewSince } from '../../../lib/use-last-visited';
import { countNewSince } from '../../../lib/summary-freshness';
import { Badge, Card, Typography } from '../ui';
import { AgentEntryRow } from '../agents/AgentEntryRow';
import entryStyles from '../agents/AgentEntryRow.module.css';
import styles from './routines.module.css';

// The compact routines summary — the count per condition and a link into the full Routines list, NOT
// the list itself. Third of the three tiles (Routines · Tasks · Files) that head Your Agents and a
// Space's Dashboard tab, and built to the same shape as `TasksSummary` beside it: the glyph on the
// heading's line (Heading 04), a row of DS status Badges under it, and the drill-in chevron centred
// against the whole card.
//
// The three counts are the three things a person wants to know about a set of routines: how many are
// live, how many they stopped, and how many the ACCOUNT stopped — the last being the only one that is
// actionable, which is why it is never folded into "Paused".
//
// The heading line also carries a "N new" badge — routines created since the reader last opened
// `/routines` on this device (`lib/last-visited.ts` + `lib/summary-freshness.ts`), the same badge the
// Tasks and Files tiles beside it wear. It costs nothing extra: the list is already in hand.

// `icon` is the 12px glyph the mobile entry-row badge leads with (Figma 15311:197080), shared 1:1 with
// native's status icons; the desktop summary draws the text-label badge and ignores it.
const CATS = [
  { key: 'active' as const, labelKey: 'routines.chip.active', variant: 'done' as const, icon: Check },
  { key: 'paused' as const, labelKey: 'routines.chip.paused', variant: 'paused' as const, icon: OctagonPause },
  { key: 'outOfCredits' as const, labelKey: 'routines.chip.outOfCredits', variant: 'waiting-on-you' as const, icon: OctagonAlert },
] satisfies { key: string; labelKey: string; variant: string; icon: ComponentType<{ size?: number }> }[];

export function RoutinesSummary({
  entryRow = false,
  workspaceId,
  workspaceSlug,
  pageName = 'agents',
}: {
  /** Mobile Your Agents: render the compact single-row entry tile with 12px icon-count badges
   *  (Figma 15311:197080), instead of the desktop heading-over-chips summary. */
  entryRow?: boolean;
  /** Scopes the read to one Space (a Space's Dashboard tab). Omit for the account-wide count. */
  workspaceId?: string;
  /** Carried into the link so the Routines screen opens pre-filtered to that Space. */
  workspaceSlug?: string;
  pageName?: string;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useRoutines(workspaceId);
  // Captured once at mount — see `useNewSince` for why reading the stamp live would make the badge
  // vanish under the reader the moment they came back from `/routines`.
  const since = useNewSince('routines');

  if (state.kind !== 'ready') return null;

  const counts = { active: 0, paused: 0, outOfCredits: 0 };
  for (const routine of state.routines) {
    const status = routineStatus(routine);
    if (status === 'out-of-credits') counts.outOfCredits += 1;
    else if (status === 'paused') counts.paused += 1;
    // A trigger-driven or unscheduled routine is not "Active" — it is counted in the total only, so a
    // chip never promises a schedule that isn't there.
    else if (status === 'active') counts.active += 1;
  }
  const total = state.routines.length;
  const empty = total === 0;
  const shown = CATS.filter((c) => counts[c.key] > 0);
  const newCount = since === null ? 0 : countNewSince(state.routines, since);

  const href = workspaceSlug
    ? `${basePath}/routines?workspace=${encodeURIComponent(workspaceSlug)}`
    : `${basePath}/routines`;
  const openRoutines = () => logPageClickBi({ page_name: pageName, button_name: 'open_routines' });

  // Mobile entry row (Figma 15311:197080): the title, then one 12px icon-count badge per non-empty
  // status on its own status-chip colour, then the drill-in chevron. Mirrors native's `entryRowCard`.
  if (entryRow) {
    return (
      <AgentEntryRow
        title={t('routines.title')}
        href={href}
        onClick={openRoutines}
        testId="agentsRoutinesSummary"
        trailing={
          <span className={entryStyles.badges}>
            {shown.map((c) => (
              <Badge
                key={c.key}
                variant={c.variant}
                label={
                  <span className={entryStyles.countBadge}>
                    <c.icon size={12} aria-hidden />
                    {counts[c.key]}
                  </span>
                }
                aria-label={t(c.labelKey, { count: counts[c.key] })}
              />
            ))}
          </span>
        }
      />
    );
  }

  return (
    <Card
      component="a"
      interactive
      className={styles.summaryCard}
      {...({ href } as Record<string, string>)}
      onClick={() => logPageClickBi({ page_name: pageName, button_name: 'open_routines' })}
    >
      <div className={styles.summaryBody}>
        <span className={styles.summaryTitle}>
          <Repeat size={16} aria-hidden />
          <Typography variant="h4" color="primary" component="span" className={styles.summaryTitleText}>
            {empty ? t('routines.title') : t('routines.heading', { count: total })}
          </Typography>
          {newCount > 0 && <Badge className={styles.summaryNew} label={t('summary.newBadge', { count: newCount })} />}
        </span>
        {empty ? (
          <Typography variant="p2" color="secondary">
            {t('routines.chip.none')}
          </Typography>
        ) : (
          <div className={styles.summaryChips}>
            {shown.map((c) => (
              <Badge key={c.key} variant={c.variant} label={t(c.labelKey, { count: counts[c.key] })} />
            ))}
          </div>
        )}
      </div>
      <ChevronRight size={16} className={`${styles.summaryChevron} dir-flip`} aria-hidden />
    </Card>
  );
}
